import { Award, Gift, Shirt } from "lucide-react";

const REWARD_LABELS = {
  free_drying: "Séchage offert",
  discount: "Réduction",
  free_kg: "Kg offert(s)",
  custom: "Récompense",
};

export function rewardText(loyalty) {
  if (!loyalty) return "";
  return loyalty.reward_value || REWARD_LABELS[loyalty.reward_type] || loyalty.reward_type || "Récompense";
}

export default function LoyaltyCard({ loyalty, settings = {}, cardLink, compact = false }) {
  if (!loyalty) return null;
  const { stamps, required, remaining, rewards_available, card_number, customer_name, mode } = loyalty;
  const reward = rewardText(loyalty);
  const company = settings.company_name || "WuraPro";
  const isKg = mode === "per_kg";
  const unitLabel = isKg ? "kg" : "passage(s)";
  const pct = required > 0 ? Math.min(100, (stamps / required) * 100) : 0;
  const useDots = !isKg && required <= 12;
  const dots = Array.from({ length: Math.max(useDots ? required : 0, 0) }, (_, i) => i < stamps);

  if (compact) {
    return (
      <div data-testid="loyalty-compact" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-amber-800 flex items-center gap-1"><Award size={15} /> Fidélité</span>
          <span className="text-amber-700">{stamps}/{required}{isKg ? " kg" : ""}</span>
        </div>
        {useDots ? (
          <div className="flex gap-1 my-2 flex-wrap">
            {dots.map((on, i) => (
              <span key={i} className={`h-4 w-4 rounded-full border ${on ? "bg-amber-500 border-amber-500" : "bg-white border-amber-300"}`} />
            ))}
          </div>
        ) : (
          <div className="my-2 h-2 rounded-full bg-amber-200 overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${pct}%` }} /></div>
        )}
        {rewards_available > 0
          ? <p className="text-emerald-700 font-medium flex items-center gap-1"><Gift size={14} /> Récompense disponible : {reward} !</p>
          : <p className="text-amber-700">Plus que {remaining} {unitLabel} pour : {reward}</p>}
      </div>
    );
  }

  return (
    <div data-testid="loyalty-card" className="rounded-2xl overflow-hidden shadow-lg max-w-sm w-full">
      <div className="bg-gradient-to-br from-sky-600 to-sky-800 text-white p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {settings.logo_url ? <img src={settings.logo_url} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <div className="h-9 w-9 rounded-lg bg-white/20 grid place-items-center"><Shirt size={18} /></div>}
            <span className="font-display font-bold text-lg">{company}</span>
          </div>
          <Award size={22} className="text-amber-300" />
        </div>
        <p className="text-white/80 text-xs mt-3">Carte de fidélité</p>
        <p className="font-mono text-sm">{card_number || "—"}</p>
        {customer_name && <p className="font-semibold mt-1">{customer_name}</p>}
      </div>
      <div className="bg-white p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-500">Progression{isKg ? " (kg)" : ""}</span>
          <span className="font-bold text-slate-800">{stamps} / {required}{isKg ? " kg" : ""}</span>
        </div>
        {useDots ? (
          <div className="flex gap-2 flex-wrap mb-3">
            {dots.map((on, i) => (
              <div key={i} className={`h-8 w-8 rounded-full grid place-items-center border-2 ${on ? "bg-amber-500 border-amber-500 text-white" : "bg-slate-50 border-slate-200 text-slate-300"}`}>
                <Award size={15} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mb-3 h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} /></div>
        )}
        {rewards_available > 0 ? (
          <div className="rounded-lg bg-emerald-50 text-emerald-700 p-3 text-sm font-medium flex items-center gap-2">
            <Gift size={18} /> {rewards_available} récompense(s) disponible(s) : {reward} !
          </div>
        ) : (
          <p className="text-sm text-slate-600">Plus que <b>{remaining} {unitLabel}</b> pour obtenir : <b>{reward}</b></p>
        )}
        {cardLink && (
          <div className="mt-4 flex flex-col items-center">
            <img alt="QR carte" className="rounded-lg" src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(cardLink)}`} />
            <p className="text-[11px] text-slate-400 mt-1">Scannez pour ouvrir votre carte</p>
          </div>
        )}
      </div>
    </div>
  );
}
